/**
 * blur-on-scroll.js
 *
 * Observes elements with the class `custom-section-content` for viewport
 * intersection, and applies a CSS filter blur (0px → 10px) to the sibling
 * or parent element with the class `custom-section-background` within the
 * same container. Blur is 0 when the content element first enters the
 * viewport and reaches 10px as it scrolls toward the top edge.
 *
 * Uses IntersectionObserver to activate/deactivate scroll tracking per
 * element, and requestAnimationFrame to throttle scroll updates.
 */

(function () {
  const BLUR_MAX = 10; // px
  const TRIGGER_SELECTOR = '.custom-section-content';
  const BLUR_SELECTOR = '.custom-section-background';

  // Track which pairs are currently intersecting and need scroll updates.
  // Each entry is { trigger, background } where trigger drives the scroll
  // progress calculation and background receives the blur.
  const activeElements = new Set();
  let rafPending = false;

  /**
   * Calculate blur amount (0–BLUR_MAX) based on how far the trigger element
   * has travelled through the viewport.
   *
   * Progress is measured from when the trigger's top edge reaches the
   * bottom of the viewport (progress = 0, blur = 0) to when the
   * trigger's top edge reaches the top of the viewport (progress = 1,
   * blur = BLUR_MAX).
   */
  function getBlurForElement(trigger) {
    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Distance from element top to viewport bottom (entry point)
    // When rect.top === viewportHeight  → progress 0  (just entering)
    // When rect.top === 0               → progress 1  (top at viewport top)
    // When rect.top < 0                 → element scrolled past, clamp to 1

    const progress = 1 - (rect.top / viewportHeight);
    const clamped = Math.min(Math.max(progress, 0), 1);

    return (clamped * BLUR_MAX).toFixed(2);
  }

  function updateBlurs() {
    activeElements.forEach(({ trigger, background }) => {
      const blur = getBlurForElement(trigger);
      background.style.filter = `blur(${blur}px)`;
    });
    rafPending = false;
  }

  function onScroll() {
    if (!rafPending && activeElements.size > 0) {
      rafPending = true;
      requestAnimationFrame(updateBlurs);
    }
  }

  // IntersectionObserver watches each trigger element entering/leaving the
  // viewport. When intersecting, the { trigger, background } pair is added
  // to the active set. When it leaves, blur on the background is reset to 0.
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const trigger = entry.target;
        // Find the associated background: look in the trigger's parent first,
        // then fall back to the closest ancestor that contains one.
        const background =
          trigger.parentElement.querySelector(BLUR_SELECTOR) ||
          trigger.closest(BLUR_SELECTOR);

        if (!background) return;

        if (entry.isIntersecting) {
          activeElements.add({ trigger, background });
          // Immediately apply correct blur for current scroll position
          requestAnimationFrame(() => {
            const blur = getBlurForElement(trigger);
            background.style.filter = `blur(${blur}px)`;
          });
        } else {
          // Remove the pair matching this trigger
          activeElements.forEach((pair) => {
            if (pair.trigger === trigger) activeElements.delete(pair);
          });
          background.style.filter = 'blur(0px)';
        }
      });
    },
    {
      // threshold: 0 fires as soon as any part of the element is visible
      threshold: 0,
    }
  );

  function init() {
    const elements = document.querySelectorAll(TRIGGER_SELECTOR);

    if (!elements.length) return;

    elements.forEach((el) => observer.observe(el));
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();